import { FormEventHandler, ReactElement } from "react";
import { Head, Link, useForm, usePage } from "@inertiajs/react";
import GuestLayout from "@/Layouts/AppGuestLayout";
import { Button } from "@/Components/Common/Buttons";
import { CheckboxField, InputField } from "@/Components/Forms";
import { useTranslations } from "@/hooks/use-translations";
import { PageProps } from "@/types";

interface LoginProps {
    status?: string;
    canResetPassword: boolean;
}

export default function Login({
    status,
    canResetPassword,
}: LoginProps): ReactElement {
    const { t } = useTranslations();
    const { tenant, authSettings } = usePage<PageProps>().props;
    const { flash } = usePage<{ flash: { error?: string } }>().props;
    const { data, setData, post, processing, errors, reset } = useForm({
        email: "",
        password: "",
        remember: false,
    });

    const displayName = tenant?.name ?? "Novazelle";
    const passwordEnabled = authSettings?.password_enabled ?? true;
    const microsoftEnabled = authSettings?.microsoft_enabled ?? false;

    const submit: FormEventHandler = (e) => {
        e.preventDefault();

        post(route("backend.login"), {
            onFinish: () => reset("password"),
        });
    };

    return (
        <GuestLayout
            title={t("common.welcome_back")}
            subtitle={t("common.sign_in_to_continue", {
                name: displayName,
            })}
        >
            <Head title={t("auth.login_title")} />

            {status && (
                <div className="mb-6 rounded-lg bg-status-success-subtle px-4 py-3 text-sm font-medium text-status-success-foreground">
                    {status}
                </div>
            )}

            {flash?.error && (
                <div className="mb-6 rounded-lg bg-status-danger-subtle px-4 py-3 text-sm font-medium text-status-danger-foreground">
                    {flash.error}
                </div>
            )}

            {microsoftEnabled && (
                <div className="space-y-5">
                    <a
                        href={route("backend.auth.microsoft.redirect")}
                        className="flex h-11 w-full items-center justify-center gap-3 rounded-md border border-border bg-background text-sm font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    >
                        <svg
                            className="h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 21 21"
                            aria-hidden="true"
                        >
                            <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                            <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                            <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                            <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                        </svg>
                        {t("auth.sign_in_with_microsoft")}
                    </a>

                    {passwordEnabled && (
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t border-border" />
                            </div>
                            <div className="relative flex justify-center">
                                <span className="bg-brand-form-surface px-3 text-xs text-muted-foreground">
                                    {t("auth.or_continue_with")}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {passwordEnabled && (
            <form onSubmit={submit} className="space-y-5">
                <InputField
                    label={t("common.email")}
                    name="email"
                    type="email"
                    value={data.email}
                    className="h-11 w-full"
                    autoComplete="username"
                    autoFocus
                    onChange={(e) => setData("email", e.target.value)}
                    error={errors.email}
                />

                <InputField
                    label={t("common.password")}
                    labelExtra={
                        canResetPassword ? (
                            <Link
                                href={route("backend.password.request")}
                                className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus:outline-none"
                            >
                                {t("auth.forgot_password_link")}
                            </Link>
                        ) : undefined
                    }
                    name="password"
                    type="password"
                    value={data.password}
                    className="h-11"
                    autoComplete="current-password"
                    onChange={(e) => setData("password", e.target.value)}
                    error={errors.password}
                />

                <CheckboxField
                    id="remember"
                    name="remember"
                    label={t("auth.remember_me")}
                    checked={data.remember}
                    onCheckedChange={(checked) => setData("remember", checked)}
                />

                <Button
                    className="h-11 w-full bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80"
                    disabled={processing}
                >
                    {processing ? (
                        <span className="flex items-center gap-2">
                            <svg
                                className="h-4 w-4 animate-spin"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                />
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v8z"
                                />
                            </svg>
                            {t("auth.signing_in")}
                        </span>
                    ) : (
                        t("common.log_in")
                    )}
                </Button>
            </form>
            )}

            {/* Divider */}
            <div className="relative mt-8">
                <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center">
                    <span className="bg-brand-form-surface px-3 text-xs text-muted-foreground">
                        {t("common.contact_organisation_admin")}
                    </span>
                </div>
            </div>

            <p className="mt-4 text-center text-sm text-muted-foreground whitespace-no-wrap">
                {t("common.contact_organisation_admin")}{" "}
                <a
                    href="mailto:sales@carzelle.com"
                    className="font-medium text-foreground underline-offset-4 hover:underline"
                >
                    sales@carzelle.com
                </a>
            </p>
        </GuestLayout>
    );
}
